import React, { Component } from 'react'
import { extendObservable } from 'mobx'
import { Formik, Form, Field } from 'formik'
import yup from 'yup'
import { inject, observer } from 'mobx-react'
import propTypes from 'prop-types'
import moment from 'moment'
import omit from 'lodash/omit'

import { getAllOffices, employeeApi, teamApi } from '@atheneum/api'

import Input from '@components/Form/Input'
import Checkbox from '@components/Form/Checkbox'
import Button from '@components/Button'
import Spinner from '@components/spinner'
import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import Tooltip from '@components/Tooltip'
import InfoIcon from '@components/Icon/Info'

const validationSchema = yup.object().shape({
  team: yup.object().shape({
    name: yup.string().required('Name is required'),
    atheneumOfficeId: yup
      .number()
      .nullable()
      .required('Atheneum office is required'),
    outlookTeamUserId: yup
      .string()
      .matches(
        /^([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})?$/i,
        'Invalid outlook team user id.\nExpected format: 1a1a1b1c-1a1d-1a1f-8a1d-1c1c1b1e1e1a (1-9, a-f)'
      )
      .nullable()
  }),
  accountManagerId: yup
    .number()
    .required('Account manager is required')
    .nullable(),
  isDifferentCurrentMonthAM: yup.boolean(),
  currentMonthAccountManagerId: yup
    .number()
    .nullable()
    .when('isDifferentCurrentMonthAM', (isDifferentCurrentMonthAM, schema) => {
      if (isDifferentCurrentMonthAM) {
        return schema.required('Account manager is required')
      } else {
        return schema
      }
    }),
  principalId: yup.number().nullable(),
  seniorPrincipalId: yup.number().nullable(),
  isDifferentCurrentMonthPR: yup.boolean(),
  currentMonthPrincipalId: yup.number().nullable()
})

class TeamModal extends Component {
  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: false
  }

  static propTypes = {
    staticData: propTypes.object.isRequired,
    modalStore: propTypes.object.isRequired,
    teamStore: propTypes.object.isRequired,
    teamId: propTypes.number,
    initialValues: propTypes.object,
    save: propTypes.func.isRequired,
    employees: propTypes.array.isRequired,
    principals: propTypes.array.isRequired,
    growlStore: propTypes.object.isRequired,
    monthYear: propTypes.string.isRequired
  }

  constructor(props) {
    super(props)

    const date = moment(props.monthYear, 'YYYY-MM')

    extendObservable(this, {
      loading: true,
      atheneumOffices: [],
      currentMonthAccountManagers: [],
      currentMonthPrincipals: [],
      isPreviousMonthCreate:
        !props.initialValues && moment().startOf('month').isAfter(date)
    })

    this.init()
  }

  init = async () => {
    const atheneumOffices = await getAllOffices()

    this.atheneumOffices.replace(atheneumOffices)

    if (this.isPreviousMonthCreate) {
      const now = moment()
      const currentMonthAccountManagers = await teamApi.getUnassignedAccountManagers(
        now.year(),
        now.month()
      )
      this.currentMonthAccountManagers.replace(currentMonthAccountManagers)

      const { employeePositions } = this.props.staticData.names
      const { PRINCIPAL } = this.props.staticData.CONST.EMPLOYEE_POSITIONS
      let currentMonthPrincipals = await employeeApi.getByPosition([PRINCIPAL])
      currentMonthPrincipals = currentMonthPrincipals.map(e => {
        e.position = employeePositions[e.employeePositionId]
        return e
      })
      this.currentMonthPrincipals.replace(currentMonthPrincipals)
    }

    this.loading = false
  }

  render() {
    if (this.loading) return <Spinner />

    return (
      <div>
        <Formik
          initialValues={initializeForm(this.props.initialValues)}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={(values, actions) => {
            values = omit(values, [
              'isDifferentCurrentMonthAM',
              'isDifferentCurrentMonthPR'
            ])
            if (!values.team.outlookTeamUserId) {
              values.team.outlookTeamUserId = null
            }

            this.props
              .save(values, this.props.teamId)
              .then(team => {
                this.props.growlStore.showSuccess(
                  this.props.teamId ? 'Team updated.' : 'Team created.'
                )
                this.props.modalStore.close()
                this.props.teamStore.scrollToTeamId(team.id)
              })
              .catch(error => {
                this.props.growlStore.showError(error)
              })
          }}
          render={form => (
            <div className="expert-form">
              <h1 className="expert-form__title">
                {this.props.initialValues ? 'Edit' : 'Create'} team
              </h1>
              <Form onSubmit={form.handleSubmit}>
                <div className="expert-form__section">
                  {!this.props.initialValues && (
                    <div className="expert-form__input-group">
                      <label className="expert-form__label">
                        Active from{' '}
                        {moment(this.props.monthYear, 'YYYY-MM').format(
                          'MMM YYYY'
                        )}
                        <Tooltip tooltip="Month from which the team is available.">
                          <div className="iconWrapper">
                            <InfoIcon />
                          </div>
                        </Tooltip>
                      </label>
                    </div>
                  )}
                  <Field
                    name="team.name"
                    label="Name"
                    placeholder="Name"
                    component={Input}
                  />
                  <Field
                    name="team.atheneumOfficeId"
                    label="Atheneum office"
                    itemToString={item => item && item.name}
                    options={this.atheneumOffices}
                    component={Dropdown}
                  />
                  <Field
                    name="team.isExpertBackedResearch"
                    label="Expert backed research"
                    component={Checkbox}
                  />
                  <Field
                    name="team.outlookTeamUserId"
                    label="Outlook user id"
                    placeholder="Outlook user id"
                    component={Input}
                  />
                </div>
                <div className="expert-form__section">
                  <div className="expert-form__input-group">
                    <label className="expert-form__label">
                      Effective month:{' '}
                      {moment(this.props.monthYear, 'YYYY-MM').format(
                        'MMM YYYY'
                      )}
                      <Tooltip tooltip="Month for which the selected Account Manager and Regional Principal are active.">
                        <div className="iconWrapper">
                          <InfoIcon />
                        </div>
                      </Tooltip>
                    </label>
                  </div>
                  <Field
                    name="seniorAccountManagerId"
                    label="Senior Account Manager"
                    placeholder="Senior Account Manager"
                    itemToString={item =>
                      item && `${item.fullName} (${item.position})`
                    }
                    options={this.props.employees.filter(
                      e => e.id !== form.values.accountManagerId
                    )}
                    creatable={false}
                    onChange={e => {
                      form.setFieldValue(
                        'seniorAccountManagerId',
                        e?.id || null,
                        false
                      )
                      if (
                        this.isPreviousMonthCreate &&
                        !form.values.isDifferentCurrentMonthAM
                      ) {
                        if (
                          this.currentMonthAccountManagers.find(
                            am => am.id === e.id
                          )
                        ) {
                          form.setFieldValue(
                            'currentMonthAccountManagerId',
                            e.id,
                            false
                          )
                        } else {
                          form.setFieldValue(
                            'currentMonthAccountManagerId',
                            null,
                            false
                          )
                        }
                      }
                    }}
                    valueIsId
                    component={Autocomplete}
                  />
                  <Field
                    name="accountManagerId"
                    label="Account Manager"
                    placeholder="Account Manager"
                    itemToString={item =>
                      item && `${item.fullName} (${item.position})`
                    }
                    options={this.props.employees.filter(
                      a => a.id !== form.values.seniorAccountManagerId
                    )}
                    creatable={false}
                    onChange={e => {
                      if (!e) return null
                      form.setFieldValue('accountManagerId', e.id, false)
                      if (
                        this.isPreviousMonthCreate &&
                        !form.values.isDifferentCurrentMonthAM
                      ) {
                        if (
                          this.currentMonthAccountManagers.find(
                            am => am.id === e.id
                          )
                        ) {
                          form.setFieldValue(
                            'currentMonthAccountManagerId',
                            e.id,
                            false
                          )
                        } else {
                          form.setFieldValue(
                            'currentMonthAccountManagerId',
                            null,
                            false
                          )
                        }
                      }
                    }}
                    valueIsId
                    component={Autocomplete}
                  />
                  {this.isPreviousMonthCreate && (
                    <Field
                      name="isDifferentCurrentMonthAM"
                      label="Different account manager for current month"
                      component={Checkbox}
                    />
                  )}
                  {this.isPreviousMonthCreate &&
                    form.values.isDifferentCurrentMonthAM && (
                      <Field
                        name="currentMonthAccountManagerId"
                        label="Account Manager for current month"
                        placeholder="Account Manager"
                        itemToString={item =>
                          item && `${item.fullName} (${item.position})`
                        }
                        options={this.currentMonthAccountManagers}
                        creatable={false}
                        onChange={e => {
                          if (!e) return null
                          form.setFieldValue(
                            'currentMonthAccountManagerId',
                            e.id,
                            false
                          )
                        }}
                        valueIsId
                        component={Autocomplete}
                      />
                    )}
                  <Field
                    name="seniorPrincipalId"
                    label="Senior Principal"
                    placeholder="Senior Principal"
                    itemToString={item =>
                      item && `${item.fullName} (${item.position})`
                    }
                    options={this.props.principals}
                    creatable={false}
                    onChange={e => {
                      form.setFieldValue(
                        'seniorPrincipalId',
                        e ? e.id : null,
                        false
                      )
                    }}
                    valueIsId
                    component={Autocomplete}
                  />
                  <Field
                    name="principalId"
                    label="Regional Principal"
                    placeholder="Regional Principal"
                    itemToString={item =>
                      item && `${item.fullName} (${item.position})`
                    }
                    options={this.props.principals}
                    creatable={false}
                    onChange={e => {
                      form.setFieldValue('principalId', e ? e.id : null, false)
                    }}
                    valueIsId
                    component={Autocomplete}
                  />
                  {this.isPreviousMonthCreate && (
                    <Field
                      name="isDifferentCurrentMonthPR"
                      label="Different principal for current month"
                      component={Checkbox}
                    />
                  )}
                  {this.isPreviousMonthCreate &&
                    form.values.isDifferentCurrentMonthPR && (
                      <Field
                        name="currentMonthPrincipalId"
                        label="Regional principal for current month"
                        placeholder="Regional principal"
                        itemToString={item =>
                          item && `${item.fullName} (${item.position})`
                        }
                        options={this.currentMonthPrincipals}
                        creatable={false}
                        onChange={e => {
                          form.setFieldValue(
                            'currentMonthPrincipalId',
                            e ? e.id : null,
                            false
                          )
                        }}
                        valueIsId
                        component={Autocomplete}
                      />
                    )}
                </div>
                <div className="expert-form__section">
                  <Button primary submit>
                    Save
                  </Button>
                  <Button secondary onClick={this.props.modalStore.close}>
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

function initializeForm(initialValues) {
  return (
    initialValues || {
      team: {
        name: '',
        atheneumOfficeId: null,
        isExpertBackedResearch: false,
        outlookTeamUserId: null
      },
      accountManagerId: null,
      isDifferentCurrentMonthAM: false,
      currentMonthAccountManagerId: null,
      principalId: null,
      seniorPrincipalId: null,
      isDifferentCurrentMonthPR: false,
      currentMonthPrincipalId: null
    }
  )
}

export default inject(
  'staticData',
  'modalStore',
  'growlStore'
)(observer(TeamModal))
